"use client";

import React, { useState } from "react";
import { ArrowRight, BookOpen, Download, FileCheck, FileText, Layers, Microscope, Search } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
const staticResourceCategories = [{
  title: "PAT Knowledge Centre",
  description: "In-depth guides on process analytical technology, real-time release testing, and inline monitoring.",
  link: "/PatKnowledgeCentrePage",
  badge: "Technical Guide",
  iconName: "microscope"
}, {
  title: "Powder Testing Knowledge Centre",
  description: "Methodologies for flowability, density, shear cell, and compaction properties in regulated labs.",
  link: "/PowderTestingKnowledgeCentrePage",
  badge: "Application Notes",
  iconName: "layers"
}, {
  title: "Raw Material ID Knowledge Centre",
  description: "Regulatory compliance workflows and rapid spectroscopic identification protocols.",
  link: "/RawMaterialIdentificationKnowledgeCentrePage",
  badge: "Compliance Guide",
  iconName: "file-check"
}, {
  title: "HPLC Sustainability Centre",
  description: "Solvent recovery data sheets, ecological impact analysis, and operational cost savings.",
  link: "/HplcSustainabilityKnowledgeCentrePage",
  badge: "Whitepaper",
  iconName: "book-open"
}];
function CategoryIcon({
  type
}: {
  type: string;
}) {
  if (type === "microscope") {
    return <Microscope className="size-5 text-primary" />;
  }
  if (type === "layers") {
    return <Layers className="size-5 text-primary" />;
  }
  if (type === "file-check") {
    return <FileCheck className="size-5 text-primary" />;
  }
  return <BookOpen className="size-5 text-primary" />;
}
function PostsListConsumer() {
  const {
    posts,
    loading,
    error,
    hasNext,
    hasPrev,
    nextPage,
    prevPage,
    isRefetching
  } = usePosts();
  if (loading) {
    return <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
        {Array.from({
        length: 3
      }).map((_, i) => <div key={i} data-index={i} className="h-80 rounded-xl border border-border bg-muted/40 animate-pulse" />)}
      </div>;
  }
  if (error) {
    return <div className="rounded-xl border border-border bg-card p-8 text-center text-card-foreground shadow-xs">
        <p className="text-sm text-muted-foreground">
          Unable to load live articles right now. Please explore our curated knowledge centres above.
        </p>
      </div>;
  }
  if (!posts || posts.length === 0) {
    return <div className="rounded-xl border border-dashed border-border bg-muted/20 p-8 text-center">
        <p className="text-sm font-medium text-muted-foreground">
          New technical notes and laboratory publications are currently being prepared.
        </p>
      </div>;
  }
  return <div className="flex flex-col gap-8">
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
        {posts.map((post, i) => <Card key={post.id} data-index={i} className="flex flex-col justify-between border-border bg-card transition-[border-color,box-shadow] hover:border-primary/40 hover:shadow-md">
            {post.featuredImage && <div className="relative aspect-video w-full overflow-hidden rounded-t-xl bg-muted">
                <img src={post.featuredImage} alt={post.title} loading="lazy" decoding="async" className="h-full w-full object-cover" />
              </div>}
            <CardHeader>
              <div className="flex items-center justify-between gap-2 pb-2">
                <Badge variant="secondary" className="font-normal text-xs">
                  {post.categories?.[0]?.name || "Laboratory Tech"}
                </Badge>
                {post.date && <span className="text-xs text-muted-foreground">
                    {new Date(post.date).toLocaleDateString("en-IE", {
                month: "short",
                day: "numeric",
                year: "numeric"
              })}
                  </span>}
              </div>
              <CardTitle className="font-default text-lg font-semibold tracking-tight text-card-foreground">
                {post.title}
              </CardTitle>
              {post.excerpt && <CardDescription className="line-clamp-3 text-sm text-muted-foreground">
                  {post.excerpt.replace(/<[^>]+>/g, "")}
                </CardDescription>}
            </CardHeader>
            <CardFooter className="pt-2">
              <Link to={post.link || "/ResourcesPage"} className="w-full">
                <Button variant="outline" size="sm" className="w-full justify-between group">
                  <span>Read Article</span>
                  <ArrowRight className="size-4 transition-transform group-hover:translate-x-1" />
                </Button>
              </Link>
            </CardFooter>
          </Card>)}
      </div>

      {(hasNext || hasPrev) && <div className="flex items-center justify-center gap-4 pt-4">
          <Button variant="outline" size="sm" onClick={prevPage} disabled={!hasPrev || isRefetching}>
            Previous
          </Button>
          <Button variant="outline" size="sm" onClick={nextPage} disabled={!hasNext || isRefetching}>
            {isRefetching ? "Loading..." : "Next"}
          </Button>
        </div>}
    </div>;
}
export default function Resources() {
  const wp_query_params = {
    post_type: "post",
    per_page: 3,
    order: "desc",
    orderby: "date"
  };
  const wp_query = new WP_Query(wp_query_params);
  return <section id="laboratoryresources" data-nav="light" className="w-full bg-background py-16 text-foreground md:py-24">
      <div className="page-container flex flex-col gap-12">
        {/* Section Header */}
        <div className="flex flex-col gap-4 md:max-w-2xl">
          <Badge variant="outline" className="w-fit border-primary/30 text-primary">
            Resource Library
          </Badge>
          <h2 className="font-default text-3xl font-bold tracking-tight text-foreground sm:text-4xl">
            Technical guides, case studies, and application notes
          </h2>
          <p className="text-base text-muted-foreground">
            Explore documentation and knowledge bases for spectroscopic analysis, powder flowability testing, and continuous process verification in regulated laboratories.
          </p>
        </div>

        {/* Featured Documentation Knowledge Centres */}
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {staticResourceCategories.map((item, i) => <Card key={item.title} data-index={i} className="flex flex-col justify-between border-border bg-card p-1 shadow-xs transition-[border-color,box-shadow] hover:border-primary/40 hover:shadow-md">
              <CardHeader className="gap-3">
                <div className="flex items-center justify-between">
                  <div className="flex size-10 items-center justify-center rounded-lg bg-secondary text-primary">
                    <CategoryIcon type={item.iconName} />
                  </div>
                  <Badge variant="secondary" className="text-xs">
                    {item.badge}
                  </Badge>
                </div>
                <CardTitle className="font-default text-base font-semibold text-card-foreground">
                  {item.title}
                </CardTitle>
                <CardDescription className="text-xs text-muted-foreground">
                  {item.description}
                </CardDescription>
              </CardHeader>
              <CardFooter>
                <Link to={item.link} className="w-full">
                  <Button variant="ghost" size="sm" className="w-full justify-between px-0 font-medium text-primary hover:bg-transparent hover:text-primary/80">
                    <span>Explore Guide</span>
                    <ArrowRight className="size-4" />
                  </Button>
                </Link>
              </CardFooter>
            </Card>)}
        </div>

        <Separator className="my-2" />

        {/* Latest Publications Header */}
        <div className="flex flex-col items-start justify-between gap-4 md:flex-row md:items-end">
          <div>
            <h3 className="font-default text-xl font-semibold tracking-tight text-foreground sm:text-2xl">
              Latest Laboratory Publications
            </h3>
            <p className="text-sm text-muted-foreground">
              Technical articles and method validation updates from our application team.
            </p>
          </div>
          <Link to="/ResourcesPage">
            <Button variant="outline" size="sm" className="gap-2">
              <FileText className="size-4" />
              <span>All Resources</span>
            </Button>
          </Link>
        </div>

        {/* Dynamic Posts Consumer */}
        <PostsProvider wp_query={{
        post_type: "post",
        per_page: 3,
        order: "desc",
        orderby: "date",
        status: ["publish"]
      }} className="w-full">
          <PostsListConsumer />
        </PostsProvider>

        {/* Bottom Consultation Banner */}
        <div className="flex flex-col items-center justify-between gap-6 rounded-xl border border-border bg-secondary p-8 text-secondary-foreground md:flex-row">
          <div className="flex flex-col gap-1 max-w-xl">
            <h4 className="font-default text-lg font-semibold">
              Need specific validation protocols or instrument data?
            </h4>
            <p className="text-sm text-muted-foreground">
              Our application specialists provide custom feasibility studies and compliance support for your specific test matrix.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-3 shrink-0">
            <Link to="/DiscussYourApplicationPage">
              <Button variant="default">
                Discuss Your Application
              </Button>
            </Link>
            <Link to="/ApplicationConsultationPage">
              <Button variant="outline" className="bg-background">
                Book Consultation
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </section>;
}